import React, { useState } from 'react';
import { Modal } from './ui/Modal';
import { Button } from './ui/Button';
import { Search, CheckCircle, Clock, AlertCircle, User, Download } from 'lucide-react';
interface ViewHistoryEntry {
  id: string;
  contentId: string;
  contentType: 'video' | 'article';
  contentTitle: string;
  employeeId: string;
  employeeName: string;
  viewedAt: string | null;
  completionPercent: number;
  status: 'completed' | 'in_progress' | 'not_started';
  isRequired: boolean;
}
interface ViewHistoryModalProps {
  isOpen: boolean;
  onClose: () => void;
}
const MOCK_HISTORY: ViewHistoryEntry[] = [{
  id: '1',
  contentId: '1',
  contentType: 'video',
  contentTitle: 'New Employee Orientation 2024',
  employeeId: 'e1',
  employeeName: 'John Smith',
  viewedAt: '2024-03-10T10:30:00Z',
  completionPercent: 100,
  status: 'completed',
  isRequired: true
}, {
  id: '2',
  contentId: '1',
  contentType: 'video',
  contentTitle: 'New Employee Orientation 2024',
  employeeId: 'e2',
  employeeName: 'Maria Garcia',
  viewedAt: '2024-03-11T14:15:00Z',
  completionPercent: 100,
  status: 'completed',
  isRequired: true
}, {
  id: '3',
  contentId: '1',
  contentType: 'video',
  contentTitle: 'New Employee Orientation 2024',
  employeeId: 'e3',
  employeeName: 'James Wilson',
  viewedAt: '2024-03-12T09:00:00Z',
  completionPercent: 45,
  status: 'in_progress',
  isRequired: true
}, {
  id: '4',
  contentId: '1',
  contentType: 'video',
  contentTitle: 'New Employee Orientation 2024',
  employeeId: 'e4',
  employeeName: 'Emily Chen',
  viewedAt: null,
  completionPercent: 0,
  status: 'not_started',
  isRequired: true
}, {
  id: '5',
  contentId: '2',
  contentType: 'video',
  contentTitle: 'Using the HR Portal',
  employeeId: 'e1',
  employeeName: 'John Smith',
  viewedAt: '2024-03-10T11:00:00Z',
  completionPercent: 100,
  status: 'completed',
  isRequired: false
}, {
  id: '6',
  contentId: 'a1',
  contentType: 'article',
  contentTitle: 'Step-by-Step Guide to Submitting Expense Reports',
  employeeId: 'e2',
  employeeName: 'Maria Garcia',
  viewedAt: '2024-03-11T15:00:00Z',
  completionPercent: 100,
  status: 'completed',
  isRequired: true
}, {
  id: '7',
  contentId: 'a1',
  contentType: 'article',
  contentTitle: 'Step-by-Step Guide to Submitting Expense Reports',
  employeeId: 'e5',
  employeeName: 'Michael Brown',
  viewedAt: null,
  completionPercent: 0,
  status: 'not_started',
  isRequired: true
}];
export function ViewHistoryModal({
  isOpen,
  onClose
}: ViewHistoryModalProps) {
  const [activeTab, setActiveTab] = useState<'videos' | 'articles'>('videos');
  const [searchQuery, setSearchQuery] = useState('');
  const [showRequiredOnly, setShowRequiredOnly] = useState(false);
  const filteredHistory = MOCK_HISTORY.filter(entry => {
    const matchesTab = activeTab === 'videos' ? entry.contentType === 'video' : entry.contentType === 'article';
    const matchesSearch = entry.employeeName.toLowerCase().includes(searchQuery.toLowerCase()) || entry.contentTitle.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesRequired = showRequiredOnly ? entry.isRequired : true;
    return matchesTab && matchesSearch && matchesRequired;
  });
  const totalEntries = filteredHistory.length;
  const completedCount = filteredHistory.filter(e => e.status === 'completed').length;
  const completionRate = totalEntries > 0 ? Math.round(completedCount / totalEntries * 100) : 0;
  const getStatusBadge = (status: ViewHistoryEntry['status']) => {
    switch (status) {
      case 'completed':
        return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800" data-id="element-864">
            <CheckCircle className="w-3 h-3 mr-1" data-id="element-865" />
            Completed
          </span>;
      case 'in_progress':
        return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800" data-id="element-866">
            <Clock className="w-3 h-3 mr-1" data-id="element-867" />
            In Progress
          </span>;
      case 'not_started':
        return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800" data-id="element-868">
            <AlertCircle className="w-3 h-3 mr-1" data-id="element-869" />
            Not Started
          </span>;
    }
  };
  return <Modal isOpen={isOpen} onClose={onClose} title="Content Audit & View History" maxWidth="5xl" data-id="element-870">
      <div className="space-y-6" data-id="element-871">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-id="element-872">
          <div className="bg-gray-50 p-4 rounded-lg border border-gray-200" data-id="element-873">
            <p className="text-sm text-gray-500 font-medium" data-id="element-874">
              Total Assignments
            </p>
            <p className="text-2xl font-bold text-gray-900" data-id="element-875">{totalEntries}</p>
          </div>
          <div className="bg-gray-50 p-4 rounded-lg border border-gray-200" data-id="element-876">
            <p className="text-sm text-gray-500 font-medium" data-id="element-877">Completed</p>
            <p className="text-2xl font-bold text-green-600" data-id="element-878">
              {completedCount}
            </p>
          </div>
          <div className="bg-gray-50 p-4 rounded-lg border border-gray-200" data-id="element-879">
            <p className="text-sm text-gray-500 font-medium" data-id="element-880">Completion Rate</p>
            <p className="text-2xl font-bold text-blue-600" data-id="element-881">
              {completionRate}%
            </p>
          </div>
        </div>
        <div className="flex flex-col md:flex-row justify-between gap-4 items-center" data-id="element-882">
          <div className="flex bg-gray-100 p-1 rounded-lg" data-id="element-883">
            <button onClick={() => setActiveTab('videos')} className={`px-4 py-2 text-sm font-medium rounded-md transition-all ${activeTab === 'videos' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`} data-id="element-884">
              Videos
            </button>
            <button onClick={() => setActiveTab('articles')} className={`px-4 py-2 text-sm font-medium rounded-md transition-all ${activeTab === 'articles' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`} data-id="element-885">
              Articles
            </button>
          </div>
          <div className="flex items-center gap-3 w-full md:w-auto" data-id="element-886">
            <div className="relative flex-1 md:w-64" data-id="element-887">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" data-id="element-888" />
              <input type="text" placeholder="Search employee or content..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className="w-full pl-9 pr-4 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-orange-500 focus:border-orange-500" data-id="element-889" />
            </div>
            <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer select-none" data-id="element-890">
              <input type="checkbox" checked={showRequiredOnly} onChange={e => setShowRequiredOnly(e.target.checked)} className="rounded border-gray-300 text-orange-600 focus:ring-orange-500" data-id="element-891" />
              Required Only
            </label>
            <Button variant="secondary" className="hidden md:flex" data-id="element-892">
              <Download className="w-4 h-4 mr-2" data-id="element-893" />
              Export
            </Button>
          </div>
        </div>
        <div className="border border-gray-200 rounded-lg overflow-hidden" data-id="element-894">
          <div className="overflow-x-auto" data-id="element-895">
            <table className="min-w-full divide-y divide-gray-200" data-id="element-896">
              <thead className="bg-gray-50" data-id="element-897">
                <tr data-id="element-898">
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-899">
                    Employee
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-900">
                    Content Title
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-901">
                    Viewed Date
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-902">
                    Status
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200" data-id="element-903">
                {filteredHistory.length > 0 ? filteredHistory.map(entry => <tr key={entry.id} className="hover:bg-gray-50" data-id="element-904">
                      <td className="px-6 py-4 whitespace-nowrap" data-id="element-905">
                        <div className="flex items-center" data-id="element-906">
                          <div className="h-8 w-8 rounded-full bg-gray-100 flex items-center justify-center text-gray-500 mr-3" data-id="element-907">
                            <User className="w-4 h-4" data-id="element-908" />
                          </div>
                          <div className="text-sm font-medium text-gray-900" data-id="element-909">
                            {entry.employeeName}
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4" data-id="element-910">
                        <div className="text-sm text-gray-900" data-id="element-911">
                          {entry.contentTitle}
                        </div>
                        {entry.isRequired && <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-orange-100 text-orange-800 mt-1" data-id="element-912">
                            Required
                          </span>}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-913">
                        {entry.viewedAt ? new Date(entry.viewedAt).toLocaleDateString() + ' ' + new Date(entry.viewedAt).toLocaleTimeString([], {
                    hour: '2-digit',
                    minute: '2-digit'
                  }) : '-'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap" data-id="element-914">
                        {getStatusBadge(entry.status)}
                      </td>
                    </tr>) : <tr data-id="element-915">
                    <td colSpan={4} className="px-6 py-12 text-center text-gray-500" data-id="element-916">
                      No history found matching your filters.
                    </td>
                  </tr>}
              </tbody>
            </table>
          </div>
        </div>
        <div className="flex justify-end pt-4 border-t border-gray-100" data-id="element-917">
          <Button variant="secondary" onClick={onClose} data-id="element-918">
            Close
          </Button>
        </div>
      </div>
    </Modal>;
}